/**
 * AuthGuard component - protects routes requiring authentication
 *
 * Similar to RoleGuard but only requires authentication, not specific roles.
 * Used for pages that should be accessible to any authenticated user.
 *
 * Validates: Requirements 3.1, 3.2
 */

import React from 'react';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { usePermissions } from './usePermissions';

export interface AuthGuardProps {
  /** Content to render when user is authenticated */
  children: React.ReactNode;
  /** Optional custom loading component */
  loadingComponent?: React.ReactNode;
}

/**
 * Guards content based on authentication status
 *
 * - Shows loading spinner while auth state is being determined
 * - Redirects to login if user is not authenticated
 * - Renders children if user is authenticated (regardless of roles)
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  loadingComponent,
}) => {
  const { isAuthenticated, isLoading, login } = usePermissions();

  // Show loading state while auth is being determined
  if (isLoading) {
    return (
      loadingComponent ?? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Spinner size={SpinnerSize.large} label="Loading..." />
        </div>
      )
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Trigger login redirect
    login();
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Spinner size={SpinnerSize.large} label="Redirecting to login..." />
      </div>
    );
  }

  // User is authenticated
  return <div data-testid="authenticated">{children}</div>;
};
